import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, KpiCard } from "@/components/widgets";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Crosshair, Lock, Medal, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { scopeLockToast } from "@/components/dashboard/primitives";
import { TierBadge, MomDelta } from "@/components/oem/TierBadge";
import { PillarBars, FocusAreasList, KpiSectionTable } from "@/components/oem/OemWidgets";
import { OemTrendChart } from "@/components/oem/OemTrendChart";

/* ------------------------------ overview table ---------------------------- */
const OverviewTable = ({ data, selectedId, onSelect }) => (
  <div className="overflow-x-auto">
    <Table className="cx-table min-w-[720px]" data-testid="cx-overview-table">
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">#</TableHead>
          <TableHead>Location</TableHead>
          <TableHead className="text-right">{data.lens === "overall" ? "Score" : `${data.lens} Score`}</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead className="text-right">National</TableHead>
          <TableHead className="text-right">Regional</TableHead>
          <TableHead className="text-right pr-4">MoM</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.rows.map((r) => {
          const locked = r.in_scope === false;
          return (
            <TableRow key={r.outlet_id}
              className={cn(locked ? "cursor-default" : "cursor-pointer",
                r.outlet_id === selectedId && "bg-[hsl(var(--accent))] border-l-2 border-l-[hsl(var(--cx-navy))]")}
              data-locked={locked ? "true" : undefined}
              data-testid={`cx-overview-row-${r.code}`}
              onClick={() => locked
                ? scopeLockToast("Scorecard details for this location are visible to its own team and leadership.")
                : onSelect(r.outlet_id)}>
              <TableCell className="pl-4 tabular">{r.rank}</TableCell>
              <TableCell>
                <span className={cn("font-medium", locked && "text-muted-foreground")}>{r.name}</span>{" "}
                <span className="text-xs text-muted-foreground">{r.code}</span>
                {locked && <Lock size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground/70" aria-label="details locked" />}
              </TableCell>
              <TableCell className="text-right tabular font-semibold">
                {r.score !== null ? Math.round(r.score) : "—"}
                {r.max && r.max !== 1000 ? <span className="text-[0.65rem] text-muted-foreground font-normal"> / {r.max}</span> : null}
              </TableCell>
              <TableCell><TierBadge tier={r.overall_tier || r.tier} testId={`cx-tier-${r.code}`} /></TableCell>
              <TableCell className="text-right tabular text-xs">{r.national_rank ?? "—"}</TableCell>
              <TableCell className="text-right tabular text-xs">{r.regional_rank ?? "—"}</TableCell>
              <TableCell className="text-right pr-4"><MomDelta value={r.mom_delta} /></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

/* ------------------------------ location detail --------------------------- */
const HeadlineStrip = ({ doc }) => {
  const d = doc.deltas || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cx-headline-strip">
      <Card className="lg:col-span-2">
        <CardContent className="py-4 flex items-center gap-4">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.1em] text-muted-foreground">Monthly Achievement</p>
            <p className="text-4xl font-serif tabular leading-tight" data-testid="cx-detail-score">
              {doc.monthly_achievement !== null && doc.monthly_achievement !== undefined
                ? Math.round(doc.monthly_achievement) : "—"}
              <span className="text-sm text-muted-foreground font-sans"> / 1000</span>
            </p>
            <MomDelta value={d.score} suffix=" pts" testId="cx-detail-score-delta" />
          </div>
          <div className="ml-auto text-right">
            <TierBadge tier={doc.tier} className="text-xs px-2.5 py-1" testId="cx-detail-tier" />
            {d.prev_tier && d.prev_tier !== doc.tier && (
              <p className="text-[0.62rem] text-muted-foreground mt-1">was {d.prev_tier}</p>
            )}
            {doc.ytd_score !== null && doc.ytd_score !== undefined && (
              <p className="text-[0.62rem] text-muted-foreground mt-1 tabular">YTD {Math.round(doc.ytd_score)} · {doc.ytd_tier}</p>
            )}
          </div>
        </CardContent>
      </Card>
      <KpiCard label="National Rank" value={doc.national_rank ?? "—"}
        sub={<MomDelta value={d.national_rank} suffix=" places" testId="cx-nrank-delta" />}
        testId="cx-detail-national-rank" />
      <KpiCard label="Regional Rank" value={doc.regional_rank ?? "—"}
        sub={<MomDelta value={d.regional_rank} suffix=" places" testId="cx-rrank-delta" />}
        testId="cx-detail-regional-rank" />
      <Card>
        <CardContent className="py-4">
          <p className="text-[0.65rem] uppercase tracking-[0.1em] text-muted-foreground">Sales · Service</p>
          <p className="text-lg tabular font-semibold" data-testid="cx-detail-subscores">
            {doc.sales_score ?? "—"}<span className="text-[0.65rem] text-muted-foreground font-normal">/{doc.sales_max}</span>
            <span className="text-muted-foreground font-normal"> · </span>
            {doc.service_score ?? "—"}<span className="text-[0.65rem] text-muted-foreground font-normal">/{doc.service_max}</span>
          </p>
          <p className="text-[0.62rem] text-muted-foreground">
            <MomDelta value={(doc.deltas || {}).sales_score} /> · <MomDelta value={(doc.deltas || {}).service_score} />
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const LocationDetail = ({ outletId, bands }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);

  const load = useCallback(async (m) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/oem/scorecard/${outletId}`, { params: m ? { month: m } : {} });
      setDoc(r.data);
    } catch (e) {
      setDoc(null);
      setError(errMsg(e));
    }
    setLoading(false);
  }, [outletId]);

  useEffect(() => { setMonth(null); load(null); }, [load]);

  if (loading) return <Skeleton className="h-64" data-testid="cx-detail-skeleton" />;
  if (error || !doc) {
    return <EmptyState title="No scorecard here yet" subtitle={error || "This location has no uploaded scorecard."} />;
  }

  return (
    <div className="space-y-4" data-testid="cx-location-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-serif text-lg" data-testid="cx-detail-title">
          {doc.dealer_city || doc.dealer_name} <span className="text-sm text-muted-foreground">{doc.dealer_code}</span>
        </h2>
        <Select value={doc.month} onValueChange={(m) => { setMonth(m); load(m); }}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="cx-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(doc.months_available || [doc.month]).map((m) => (
              <SelectItem key={m} value={m} data-testid={`cx-month-option-${m}`}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[0.68rem] text-muted-foreground tabular" data-testid="cx-detail-stamp">
          Scorecard month: {doc.month_label} · Uploaded: {doc.uploaded_fmt}
        </span>
      </div>

      <HeadlineStrip doc={doc} />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
              <Crosshair size={15} className="text-[hsl(var(--cx-crimson))]" /> Focus Areas — points to win back
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <FocusAreasList items={doc.focus_areas} testBase="cx-detail" />
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
              <TrendingUp size={15} className="text-[hsl(var(--cx-navy))]" /> 12-Month Trend — tier zones shaded
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {(doc.trend || []).length > 1 ? (
              <OemTrendChart series={doc.trend} bands={bands} dataKey="monthly_achievement"
                testId="cx-detail-trend-chart" />
            ) : (
              <p className="text-xs text-muted-foreground py-6">Trend appears once a second month is uploaded.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(doc.pillars || {}).map(([sec, pillars]) => (
          <Card key={sec}>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-base font-normal">
                {sec} Pillars
                <span className="text-xs text-muted-foreground font-sans ml-2 tabular">
                  {sec === "Sales" ? doc.sales_score : doc.service_score} / {sec === "Sales" ? doc.sales_max : doc.service_max}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <PillarBars pillars={pillars} testBase={`cx-${sec.toLowerCase()}`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-2">
        {Object.entries(doc.sections || {}).map(([sec, pillars]) => (
          <KpiSectionTable key={sec} section={sec} pillars={pillars} month={doc.month} testBase="cx-detail" />
        ))}
      </div>

      {(doc.notes || []).length > 0 && (
        <p className="text-[0.68rem] text-muted-foreground italic" data-testid="cx-detail-notes">
          Note: {doc.notes.join(" · ")}
        </p>
      )}
    </div>
  );
};

/* ---------------------------------- page ---------------------------------- */
export default function CxScorecardsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(null);
  const [bands, setBands] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get("outlet"));

  const isLeadership = ["admin", "md", "vp_sales", "vp_service"].includes(user?.role);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const [ov, cfg] = await Promise.all([
        api.get("/oem/overview", { params: m ? { month: m } : {} }),
        api.get("/oem/config"),
      ]);
      setData(ov.data);
      setBands(cfg.data.tier_bands || []);
    } catch { setData(null); }
    setLoading(false);
  }, []);

  useEffect(() => { load(month); }, [load, month]);

  // default detail selection: URL param > own outlet > best in-scope row
  useEffect(() => {
    if (selectedId || !data?.rows?.length) return;
    const own = data.rows.find((r) => r.outlet_id === user?.outlet_id);
    const firstInScope = data.rows.find((r) => r.in_scope !== false);
    const pick = own || firstInScope;
    if (pick) setSelectedId(pick.outlet_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onSelect = (id) => {
    setSelectedId(id);
    setSearchParams({ outlet: id }, { replace: true });
  };

  const tierChips = useMemo(() => Object.entries(data?.tier_counts || {})
    .sort((a, b) => b[1] - a[1]), [data]);

  if (loading && !data) {
    return (
      <div data-testid="cx-scorecards-page">
        <PageHeader title="OEM CX Scorecard" subtitle="Kia CX KPI Evaluation" />
        <Skeleton className="h-40 mb-4" /><Skeleton className="h-64" />
      </div>
    );
  }

  if (!data || !data.month) {
    return (
      <div data-testid="cx-scorecards-page">
        <PageHeader title="OEM CX Scorecard" subtitle="Kia CX KPI Evaluation" />
        <EmptyState title="No scorecards uploaded yet"
          subtitle="The AGM Admin uploads Kia's monthly CX KPI Evaluation under Upload Data → OEM CX Scorecards." />
      </div>
    );
  }

  return (
    <div data-testid="cx-scorecards-page" className="space-y-4">
      <PageHeader title="OEM CX Scorecard"
        subtitle={`Kia CX KPI Evaluation · Scorecard month: ${data.month_label} · Uploaded: ${data.uploaded_fmt || "—"} · OEM lens — never affects complaint escalation`}>
        <Select value={data.month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="cx-overview-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(data.months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {/* group strip — aggregates visible to everyone */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Group Average" value={data.group_avg ?? "—"} testId="cx-kpi-group-avg"
          sub={data.group_trend?.length > 1 ? `${data.group_trend.map((t) => t.avg).join(" → ")}` : null} />
        <Card data-testid="cx-kpi-tier-counts">
          <CardContent className="py-4">
            <p className="text-[0.65rem] uppercase tracking-[0.1em] text-muted-foreground mb-1.5">Locations per Tier</p>
            <div className="flex flex-wrap gap-1.5">
              {tierChips.map(([tier, n]) => (
                <span key={tier} className="inline-flex items-center gap-1">
                  <TierBadge tier={tier} /><span className="text-xs tabular font-semibold">{n}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
        <KpiCard label="Best · Worst" testId="cx-kpi-best-worst"
          value={<span className="text-sm">{data.best?.name ?? "—"} <span className="text-muted-foreground">·</span> {data.worst?.name ?? "—"}</span>}
          sub={`${data.best ? Math.round(data.best.score) : "—"} · ${data.worst ? Math.round(data.worst.score) : "—"}`} />
        <KpiCard label="Biggest Movers" testId="cx-kpi-movers"
          value={<span className="text-sm">
            {data.mover_up ? `▲ ${data.mover_up.name} +${data.mover_up.mom_delta}` : "—"}
          </span>}
          sub={data.mover_down ? `▼ ${data.mover_down.name} ${data.mover_down.mom_delta}` : null} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className={isLeadership ? "lg:col-span-2" : "lg:col-span-3"}>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
              <Medal size={15} className="text-[hsl(var(--cx-gold))]" />
              All Locations — {data.month_label}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-0">
            <OverviewTable data={data} selectedId={selectedId} onSelect={onSelect} />
          </CardContent>
        </Card>

        {isLeadership && (
          <Card data-testid="cx-group-focus-panel">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
                <Crosshair size={15} className="text-[hsl(var(--cx-crimson))]" />
                Group Focus Areas — attack these first
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(data.group_focus || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No group-wide point losses this month.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data.group_focus.map((f, i) => (
                    <li key={i} className="text-xs px-2.5 py-1.5 rounded-md border border-[hsl(var(--cx-crimson)/0.2)] bg-[hsl(var(--cx-crimson)/0.03)]"
                      data-testid={`cx-group-focus-${i}`}>
                      <b>{f.name}</b>{f.section && !f.name.toLowerCase().includes(f.section.toLowerCase()) ? ` (${f.section})` : ""}
                      {f.zero_count > 0 && <Badge variant="outline" className="ml-1.5 text-[0.58rem] text-[hsl(var(--cx-crimson))] border-[hsl(var(--cx-crimson)/0.4)]">0 at {f.zero_count}</Badge>}
                      <span className="block text-muted-foreground">
                        {f.points_lost} points lost group-wide across {f.locations} location{f.locations !== 1 ? "s" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {(data.group_trend || []).length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
              <TrendingUp size={15} className="text-[hsl(var(--cx-navy))]" /> Group Average Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <OemTrendChart series={data.group_trend} bands={bands} dataKey="avg" height={170}
              testId="cx-group-trend-chart" />
          </CardContent>
        </Card>
      )}

      {selectedId && <LocationDetail outletId={selectedId} bands={bands} />}
    </div>
  );
}
